import { Check } from 'lucide-react';
import { Modal } from '../ui/Modal';
import type { StudioLaunchStep } from '../../services/studioService';

const STEPS: { id: StudioLaunchStep; label: string }[] = [
  { id: 'workspace', label: 'Creating your guest workspace' },
  { id: 'open', label: 'Opening Studio' },
];

type StepStatus = 'pending' | 'active' | 'done';

function stepStatus(stepId: StudioLaunchStep, activeStep: StudioLaunchStep): StepStatus {
  const order = STEPS.map((s) => s.id);
  const activeIdx = order.indexOf(activeStep);
  const idx = order.indexOf(stepId);
  if (idx < activeIdx) return 'done';
  if (idx === activeIdx) return 'active';
  return 'pending';
}

export interface StudioLaunchModalProps {
  isOpen: boolean;
  activeStep: StudioLaunchStep;
  subtitle: string;
  error?: string | null;
  onDismissError?: () => void;
}

export function StudioLaunchModal({
  isOpen,
  activeStep,
  subtitle,
  error,
  onDismissError,
}: StudioLaunchModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      showCloseButton={false}
      closeOnOverlayClick={false}
      maxWidth="480px"
    >
      <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
        <div
          aria-hidden
          style={{
            width: 64,
            height: 64,
            margin: '0 auto 22px',
            borderRadius: '50%',
            background:
              'conic-gradient(from 90deg, var(--primary) 0%, var(--primary-light) 45%, rgba(20,184,166,.15) 70%, transparent 72%)',
            WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 6px), #000 calc(100% - 5px))',
            mask: 'radial-gradient(farthest-side, transparent calc(100% - 6px), #000 calc(100% - 5px))',
            animation: 'spin 1.1s linear infinite',
          }}
        />

        <h2
          style={{
            fontSize: '22px',
            fontWeight: 700,
            color: 'var(--marketing-heading, #0f172a)',
            marginBottom: 10,
            letterSpacing: '-.01em',
          }}
        >
          Spinning up your agent…
        </h2>
        <p
          style={{
            color: 'var(--text-muted, #64748b)',
            fontSize: '15px',
            lineHeight: 1.55,
            marginBottom: 28,
            maxWidth: 360,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          {subtitle}
        </p>

        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            textAlign: 'left',
          }}
        >
          {STEPS.map((step) => {
            const status = stepStatus(step.id, activeStep);
            const done = status === 'done';
            const active = status === 'active';
            return (
              <li
                key={step.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  color: done || active ? '#334155' : '#94a3b8',
                  fontSize: 15,
                  fontWeight: done || active ? 600 : 500,
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: done ? 'var(--primary)' : 'transparent',
                    border: done
                      ? 'none'
                      : active
                        ? '2px solid var(--primary)'
                        : '2px solid #cbd5e1',
                    color: done ? '#fff' : active ? 'var(--primary)' : '#cbd5e1',
                  }}
                >
                  <Check size={13} strokeWidth={3} />
                </span>
                {step.label}
              </li>
            );
          })}
        </ul>

        {error && (
          <div style={{ marginTop: 22 }}>
            <p
              role="alert"
              style={{
                color: '#b91c1c',
                fontSize: 14,
                lineHeight: 1.5,
                marginBottom: onDismissError ? 14 : 0,
              }}
            >
              {error}
            </p>
            {onDismissError && (
              <button
                type="button"
                onClick={onDismissError}
                style={{
                  padding: '10px 18px',
                  borderRadius: 10,
                  border: '1px solid #e2e8f0',
                  background: '#fff',
                  color: '#334155',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
